package com.schoolconnect.app.feature.timetable

import com.schoolconnect.app.datasource.network.response.WeeklyTimetableResponse
import com.schoolconnect.app.datasource.network.response.WeeklyTimetableSlotResponse
import com.schoolconnect.app.domain.SchoolKeys
import com.schoolconnect.app.repository.classsection.ClassSectionRepository
import com.schoolconnect.app.repository.timetable.TimetableRepository
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.Dispatchers
import kotlinx.coroutines.Job
import kotlinx.coroutines.SupervisorJob
import kotlinx.coroutines.cancel
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.flow.update
import kotlinx.coroutines.launch

class TeacherTimeTableViewModel(
    private val roleCode: String,
    private val schoolId: String = "",
    private val academicYearId: String = ""
) {
    private val scope = CoroutineScope(SupervisorJob() + Dispatchers.Default)
    private val isTeacher = roleCode == SchoolKeys.V_ROLE_TEACHER
    private var classesJob: Job? = null
    private var scheduleJob: Job? = null
    private val _uiState = MutableStateFlow(TeacherTimeTableUiState())
    val uiState: StateFlow<TeacherTimeTableUiState> = _uiState.asStateFlow()

    init { loadClasses() }

    fun onEvent(event: TeacherTimeTableUiEvent) {
        when (event) {
            is TeacherTimeTableUiEvent.ClassSelected -> {
                val id = event.classSectionId.trim()
                if (id.isBlank() || _uiState.value.classes.none { it.id == id } || id == _uiState.value.selectedClassId) return
                _uiState.update { it.copy(selectedClassId = id, weekly = ParentTimeTableGridUi(), special = ParentTimeTableGridUi(), weekLabel = null, lastUpdatedLabel = null, hasPublishedTimetable = null, errorMessage = null) }
                loadSchedule(id)
            }
            TeacherTimeTableUiEvent.Retry -> if (_uiState.value.classes.isEmpty()) loadClasses() else loadSchedule(_uiState.value.selectedClassId)
        }
    }

    private fun loadClasses() {
        classesJob?.cancel()
        _uiState.update { it.copy(isLoadingClasses = true, errorMessage = null, classes = emptyList(), selectedClassId = "") }
        classesJob = scope.launch {
            val result = if (isTeacher) {
                TimetableRepository.loadTeacherClasses().map { rows ->
                    rows.mapNotNull { row ->
                        val id = row.classSectionId?.trim().orEmpty()
                        if (id.isBlank()) null else TeacherTimeTableClassOption(id, row.className.orEmpty(), row.sectionName.orEmpty())
                    }.distinctBy { it.id }.sortedWith(::compareTeacherClassOptions)
                }
            } else {
                ClassSectionRepository.loadSchoolClassSectionsForAcademicYear(schoolId, academicYearId).map { rows ->
                    rows.mapNotNull { row ->
                        val id = row.id?.trim().orEmpty()
                        if (id.isBlank()) null else TeacherTimeTableClassOption(id, row.className.orEmpty(), row.sectionName.orEmpty())
                    }.sortedWith(::compareTeacherClassOptions)
                }
            }
            result.onSuccess { classes ->
                _uiState.update { it.copy(classes = classes, selectedClassId = classes.firstOrNull()?.id.orEmpty(), isLoadingClasses = false, errorMessage = null) }
                if (classes.isNotEmpty()) loadSchedule(classes.first().id)
            }.onFailure { error ->
                _uiState.update { it.copy(classes = emptyList(), selectedClassId = "", isLoadingClasses = false, errorMessage = error.message ?: "Unable to load classes.") }
            }
        }
    }

    private fun loadSchedule(classSectionId: String) {
        scheduleJob?.cancel()
        if (classSectionId.isBlank()) {
            _uiState.update { it.copy(isLoadingSchedule = false) }
            return
        }
        _uiState.update { it.copy(isLoadingSchedule = true, errorMessage = null, weekly = ParentTimeTableGridUi(), special = ParentTimeTableGridUi(), weekLabel = null, lastUpdatedLabel = null, hasPublishedTimetable = null) }
        scheduleJob = scope.launch {
            TimetableRepository.loadTeacherWeek(classSectionId).onSuccess(::applyTeacherSchedule)
                .onFailure { error -> _uiState.update { it.copy(isLoadingSchedule = false, errorMessage = error.message ?: "Unable to load this timetable.") } }
        }
    }

    private fun applyTeacherSchedule(response: WeeklyTimetableResponse) {
        val days = response.days.orEmpty()
        val specialDayIndexes = days.indices.filter { days[it].isSpecialDay }.toSet()
        val regularDayIndexes = days.indices.filterNot { it in specialDayIndexes }
        val regularSlots = response.regularSlots.orEmpty().sortedBy { it.slotOrder }
        val specialSlots = response.specialSlots.orEmpty().sortedBy { it.slotOrder }
        _uiState.update { state ->
            state.copy(
                isLoadingSchedule = false,
                hasPublishedTimetable = response.hasPublishedTimetable,
                weekLabel = dateRangeLabel(response.weekStart, response.weekEnd),
                specialScheduleLabel = dayLabels(response.specialScheduleDays),
                weeklyOffLabel = dayLabels(response.weeklyOffDays),
                lastUpdatedLabel = if (response.isPublished) "Published" else null,
                weekly = weeklySlotsToGrid(regularSlots, days, regularDayIndexes),
                special = weeklySlotsToGrid(specialSlots, days, specialDayIndexes.toList())
            )
        }
    }

    fun clear() = scope.cancel()
}

private fun compareTeacherClassOptions(
    left: TeacherTimeTableClassOption,
    right: TeacherTimeTableClassOption
): Int = compareTimetableClassSections(
    left.className,
    left.sectionName,
    right.className,
    right.sectionName
)

private val timetableDayNames = listOf("Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun")

private fun weeklySlotsToGrid(
    slots: List<WeeklyTimetableSlotResponse>,
    days: List<com.schoolconnect.app.datasource.network.response.WeeklyTimetableDayResponse>,
    dayIndexes: List<Int>
): ParentTimeTableGridUi {
    val orderedSlots = slots.sortedBy { it.slotOrder }
    val rows = dayIndexes.mapNotNull { index ->
        val day = days.getOrNull(index) ?: return@mapNotNull null
        val cells = day.cells.orEmpty()
        val holidayCell = cells.firstOrNull { it.isOffDay || it.isHoliday }
        val holidayEntry = holidayCell?.entryText?.takeIf(String::isNotBlank)
        ParentTimeTableScheduleRow(
            day = day.dayName?.take(3) ?: timetableDayNames.getOrElse(index) { "Day" },
            subjects = orderedSlots.mapIndexed { slotIndex, slot ->
                cells.getOrNull(slotIndex)?.entryText ?: slot.regularDayCells?.getOrNull(index)?.entryText.orEmpty()
            },
            holiday = if (holidayCell != null && (holidayEntry.equals("School Holiday", ignoreCase = true) || cells.all { it.entryText.isNullOrBlank() })) holidayEntry ?: "School Holiday" else null
        )
    }
    return ParentTimeTableGridUi(orderedSlots.map { formatTimeRange(it.fromTime, it.toTime) }, rows)
}

private fun formatTimeRange(from: String?, to: String?): String = "${formatTime(from)} – ${formatTime(to)}"

private fun formatTime(value: String?): String {
    val parts = value.orEmpty().trim().split(':')
    val hour = parts.getOrNull(0)?.toIntOrNull() ?: return value.orEmpty()
    val minute = parts.getOrNull(1)?.take(2) ?: return value.orEmpty()
    val displayHour = (hour % 12).let { if (it == 0) 12 else it }
    return "${displayHour.toString().padStart(2, '0')}:$minute ${if (hour < 12) "AM" else "PM"}"
}

private fun dayLabels(indexes: List<Int>?): String = indexes.orEmpty().mapNotNull { timetableDayNames.getOrNull(it) }.joinToString(", ").ifBlank { "—" }

private fun dateRangeLabel(start: String?, end: String?): String? {
    val formattedStart = start?.toDisplayDate()
    val formattedEnd = end?.toDisplayDate()
    return when {
        !formattedStart.isNullOrBlank() && !formattedEnd.isNullOrBlank() -> "$formattedStart – $formattedEnd"
        !formattedStart.isNullOrBlank() -> formattedStart
        else -> null
    }
}

private fun String.toDisplayDate(): String {
    val parts = split('-')
    if (parts.size != 3) return this
    val month = listOf("Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec").getOrNull(parts[1].toIntOrNull()?.minus(1) ?: -1) ?: return this
    return "${parts[2]} $month ${parts[0]}"
}

